import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, CircularProgress } from '@mui/material';
import { ArrowForward, Calculate, Science, MenuBook } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { selectTest } from '../../store/slices/testSlice';
import { batchCreateTestSessionData } from '../../store/slices/testSessionsSlice';
import { liveOlympiadSeriesConfig } from '../../data/seriesConfig';
import { OlympiadData } from '../../models/OlympiadData';
import { MockTestData } from '../../models/MockTestData';
import { RootState, AppDispatch } from '../../store';
import { selectIsMobileClassroom } from '../../store/selectors/studentSelectors';
import './style.css';

export type OlympiadCardProps = OlympiadData & {
  date?: string;
  buttonText?: string;
  variant?: 'maths' | 'science' | 'english';
};

const OlympiadCard: React.FC<OlympiadCardProps> = ({
  title,
  color,
  description,
  totalQuestions,
  completedQuestions,
  date = '12 NOV',
  buttonText = 'Result',
  variant = 'maths'
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const selectedStudent = useSelector((state: RootState) => state.students.selectedStudent);
  const isMobileClassroom = useSelector(selectIsMobileClassroom);

  const [isCreatingSession, setIsCreatingSession] = useState(false);

  function getSubjectName() {
    switch (variant) {
      case 'maths':
        return 'Math';
      case 'science':
        return 'Science';
      case 'english':
        return 'English';
      default:
        return 'Math';
    }
  }

  // Allow all students to access all tests - no subject restrictions
  const isCompleted = completedQuestions === totalQuestions;
  const isDisabled = isCompleted;

  const getIcon = () => {
    switch (variant) {
      case 'maths':
        return <Calculate sx={{ fontSize: 40, color: 'white' }} />;
      case 'science':
        return <Science sx={{ fontSize: 40, color: 'white' }} />;
      case 'english':
        return <MenuBook sx={{ fontSize: 40, color: 'white' }} />;
      default:
        return <Calculate sx={{ fontSize: 40, color: 'white' }} />;
    }
  };

  const getBackgroundColor = () => {
    switch (variant) {
      case 'maths':
        return '#2563EB'; // Blue color for Maths
      case 'science':
        return '#06B6D4';
      case 'english':
        return '#FF8C00'; // Orange color for English
      default:
        return color;
    }
  };

  const getQuestionText = () => {
    return `${completedQuestions || 0}/${totalQuestions || 0} Questions`;
  };

  const handleStartOlympiad = async () => {
    if (isDisabled || isCreatingSession) {
      return;
    }

    if (!selectedStudent) {
      return;
    }

    // Get fresh state from Redux store
    const currentState = store.getState();
    const currentTestSessions = currentState.testSessions.sessionsBySeriesAndSubject;

    // Check if session already exists for this subject
    const existingSession = currentTestSessions['NextZenOlympiad Final']?.[getSubjectName()];

    if (existingSession?.data?.sessionId) {
      // Session already exists, use it directly
      navigateToTest(existingSession.data.sessionId);
      return;
    }

    // Create session using the same flow as Mock Tests
    setIsCreatingSession(true);

    try {
      // Use the same session creation flow as Mock Tests
      await dispatch(batchCreateTestSessionData({
        studentId: selectedStudent.id,
        grade: selectedStudent.grade,
        studentName: selectedStudent.name,
        studentPhone: selectedStudent.phoneNumber,
        series: liveOlympiadSeriesConfig,
        autoAddQuestions: true
      })).unwrap();

      // Get fresh state after session creation
      const updatedState = store.getState();
      const updatedTestSessions = updatedState.testSessions.sessionsBySeriesAndSubject;

      const subjectName = getSubjectName();
      const sessionData = updatedTestSessions['NextZenOlympiad Final']?.[subjectName];

      if (sessionData?.data?.sessionId) {
        navigateToTest(sessionData.data.sessionId);
      }
    } catch (error) {
      // Error creating session
    } finally {
      setIsCreatingSession(false);
    }
  };


  const navigateToTest = (sessionId: string) => {
    // Convert OlympiadData to MockTestData format for compatibility
    const testData: MockTestData = {
      sessionId,
      title,
      subject: variant || 'maths',
      progress: 0,
      color: getBackgroundColor(),
      description,
      totalQuestions,
      completedQuestions,
      nextTestIn: '0 days',  // NextZenOlympiad is always available
      date,
      buttonText,
      variant,
      status: 'open'  // NextZenOlympiad Final is always open
    };

    // Select test in Redux first, then navigate
    dispatch(selectTest(testData));
    navigate('/start-test');
  };

  return (
    <Card
      className="olympiad-card-new"
      onClick={isDisabled || isCreatingSession ? undefined : handleStartOlympiad}
      sx={{
        background: isDisabled ? '#cccccc' : getBackgroundColor(),
        color: isDisabled ? '#666666' : 'white',
        borderRadius: 3,
        minHeight: 200,
        position: 'relative',
        overflow: 'visible',
        cursor: isDisabled || isCreatingSession ? (isCreatingSession ? 'wait' : 'not-allowed') : 'pointer',
        opacity: isDisabled || isCreatingSession ? 0.6 : 1,
        pointerEvents: isCreatingSession ? 'none' : 'auto',
        transition: 'all 0.2s ease-in-out',
        '&:hover': isDisabled || isCreatingSession ? {} : {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header with title and date */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
              {description}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', minWidth: 60 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1, fontSize: '0.9rem' }}>
              {date.split(' ').slice(0, 2).join(' ')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.75rem', mt: 0.5 }}>
              {date.split(' ')[2] || '2024'}
            </Typography>
          </Box>
        </Box>

        {/* Icon */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          {getIcon()}
        </Box>

        {/* Bottom section with National Level label, Question chip, and Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.8,
              fontSize: '0.75rem',
              fontWeight: 500,
              letterSpacing: '0.5px'
            }}
          >
            National Level
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={getQuestionText()}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
                '& .MuiChip-label': {
                  px: 1
                }
              }}
            />
            <Button
              variant="contained"
              endIcon={isCreatingSession ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
              disabled={isCreatingSession || isDisabled}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                },
                '&:disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.6)'
                }
              }}
            >
              {isCreatingSession ? 'Starting...' :
               isCompleted ? 'Completed' :
               buttonText}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OlympiadCard;